export class UseContextNotifier<T> {
  public state: T;
  public setState: React.Dispatch<React.SetStateAction<T>>;
  private _listener: ((value: T) => void) | undefined;

  constructor(state: T, setState: React.Dispatch<React.SetStateAction<T>>) {
    this.state = state;
    this.setState = setState;
  }

  private _isMergeableObject(value: unknown): value is Record<string, unknown> {
    return (
      typeof value === 'object' &&
      value !== null &&
      !Array.isArray(value) &&
      !(value instanceof Date)
    );
  }

  public updateState(newState: T) {
    // Many usecases call updateState({...this.state, ...partial}).
    // When multiple async requests resolve concurrently, using a plain "replace"
    // can cause lost updates. We shallow-merge object states to reduce clobbering.
    const mergedState =
      this._isMergeableObject(this.state) && this._isMergeableObject(newState)
        ? ({ ...this.state, ...newState } as T)
        : newState;

    this.state = mergedState;

    this.setState(prev => {
      if (this._isMergeableObject(prev) && this._isMergeableObject(newState)) {
        return { ...prev, ...newState } as T;
      }
      return newState;
    });
    if (this._listener) {
      this._listener(mergedState);
    }
  }

  seedState(newState: T) {
    this.state = newState;
    this.setState(newState);
  }

  setListener(listener: (value: T) => void) {
    this._listener = listener;
  }
}
